import { useCallback, useState } from 'react';
import type { DmQuickReplyMedia } from '../../../../../services/dmQuickReplies';
import { defaultAttachmentName, withMimeType } from './incomingFiles.ts';
import { stageFile, stageQuickReplyMedia, type StagedAttachment } from './stagedAttachment.ts';
import { validateAttachment } from './validateAttachment.ts';

export interface AttachmentNotice {
    tone: 'error' | 'info';
    text: string;
}

export interface ComposerAttachment {
    staged: StagedAttachment | null;
    notice: AttachmentNotice | null;
    setFile: (file: Blob, name?: string) => boolean;
    setFiles: (files: readonly File[]) => void;
    setQuickReplyMedia: (media: DmQuickReplyMedia) => void;
    clear: () => void;
    dismissNotice: () => void;
}

function nameOf(file: Blob, name: string | undefined): string {
    if (name) return name;
    if (file instanceof File && file.name) return file.name;
    return defaultAttachmentName(file.type);
}

// One attachment per message: every successful set replaces what was staged.
export function useComposerAttachment(): ComposerAttachment {
    const [staged, setStaged] = useState<StagedAttachment | null>(null);
    const [notice, setNotice] = useState<AttachmentNotice | null>(null);

    const setFile = useCallback((file: Blob, name?: string) => {
        const fileName = nameOf(file, name);
        const result = validateAttachment({ name: fileName, type: file.type, size: file.size });
        if (!result.ok) {
            setNotice({ tone: 'error', text: result.error });
            return false;
        }
        const typed = withMimeType(file, fileName, result.mimeType);
        setStaged((prev) =>
            stageFile(prev, { file: typed, name: fileName, mimeType: result.mimeType })
        );
        setNotice(null);
        return true;
    }, []);

    const setFiles = useCallback(
        (files: readonly File[]) => {
            const [first] = files;
            if (!first || !setFile(first)) return;
            if (files.length > 1) {
                setNotice({
                    tone: 'info',
                    text: `One attachment per message: attached "${nameOf(first, undefined)}".`
                });
            }
        },
        [setFile]
    );

    const setQuickReplyMedia = useCallback((media: DmQuickReplyMedia) => {
        setStaged((prev) => stageQuickReplyMedia(prev, media));
        setNotice(null);
    }, []);

    const clear = useCallback(() => {
        setStaged(null);
        setNotice(null);
    }, []);

    const dismissNotice = useCallback(() => setNotice(null), []);

    return { staged, notice, setFile, setFiles, setQuickReplyMedia, clear, dismissNotice };
}
